import React, { useCallback, useEffect, useState } from "react";

import { GridApi, IHeaderParams } from "@ag-grid-community/core";
import cn from "classnames";

import BaseHeader from "./BaseHeader";

const getSelectAllStatus = (api: GridApi): boolean | undefined => {
  const notOnLastPage = api.paginationGetCurrentPage() < api.paginationGetTotalPages() - 1;
  const totalRowCount = api.getModel().getRowCount();
  const rowsOnPage = api.paginationGetPageSize();

  let visibleRows = 0;
  if (totalRowCount > 0) {
    if (totalRowCount % rowsOnPage === 0) {
      // in this case, the number of rows on each page will be the same and
      // equal to the pageSize
      visibleRows = rowsOnPage;
    } else if (notOnLastPage) {
      // if the last page has fewer rows than other pages, but we're not on the last page,
      // then visiblePage is again equal to the pageSize
      visibleRows = rowsOnPage;
    } else {
      // Otherwise, we're on the last page. Let's find out how many rows are displayed there
      visibleRows = totalRowCount % rowsOnPage;
    }
  }

  const selectedRowsCount = api.getSelectedNodes().length;

  return visibleRows > 0 && visibleRows === selectedRowsCount
    ? true
    : selectedRowsCount > 0
    ? undefined
    : false;
};

const setRowsSelection = (
  api: GridApi,
  startRow: number,
  endRow: number,
  selected: boolean
): void => {
  api.forEachNode((rowNode) => {
    if (rowNode.rowIndex >= startRow && rowNode.rowIndex < endRow) {
      rowNode.setSelected(selected);
    }
  });
};

interface Props extends IHeaderParams {}

const CheckboxSelectionHeader: React.FC<Props> = (props) => {
  const { api } = props;

  // true = all, false = none, undefined = some (indeterminate state)
  const [selectAllChecked, setSelectAllChecked] = useState<boolean | undefined>(
    getSelectAllStatus(api)
  );

  const updateSelectAllCheckboxStatus = useCallback(() => {
    setSelectAllChecked(getSelectAllStatus(api));
  }, [api, setSelectAllChecked]);

  const handlePaginationChange = useCallback(() => {
    // reset selection on pagination change
    api.forEachNode((rowNode) => rowNode.setSelected(false));
    updateSelectAllCheckboxStatus();
  }, [api, updateSelectAllCheckboxStatus]);

  // listen to various events to update the checkbox state according to current grid state
  useEffect(() => {
    api.addEventListener("modelUpdated", updateSelectAllCheckboxStatus);
    api.addEventListener("selectionChanged", updateSelectAllCheckboxStatus);
    api.addEventListener("paginationChanged", handlePaginationChange);
    return () => {
      api.removeEventListener("modelUpdated", updateSelectAllCheckboxStatus);
      api.removeEventListener("selectionChanged", updateSelectAllCheckboxStatus);
      api.removeEventListener("paginationChanged", handlePaginationChange);
    };
  }, [api, handlePaginationChange, updateSelectAllCheckboxStatus]);

  const handleSelectAllClick = useCallback(() => {
    // only select row nodes of the current page
    const startRow = api.paginationGetCurrentPage() * api.paginationGetPageSize();
    const endRow = startRow + api.paginationGetPageSize();
    setRowsSelection(api, startRow, endRow, !selectAllChecked);
  }, [api, selectAllChecked]);

  return (
    <BaseHeader
      headingRender={() => (
        <div
          className="ag-header-select-all ag-labeled ag-label-align-right ag-checkbox ag-input-field"
          onClick={handleSelectAllClick}
          onTouchEnd={handleSelectAllClick}
          role="presentation"
        >
          <div className="ag-input-field-label ag-label ag-hidden ag-checkbox-label" />
          <div
            className={cn("ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper", {
              "ag-checked": selectAllChecked === true,
              "ag-indeterminate": selectAllChecked === undefined,
            })}
            role="presentation"
          >
            <input
              className="ag-input-field-input ag-checkbox-input"
              tabIndex={-1}
              type="checkbox"
            />
          </div>
        </div>
      )}
      {...props}
    />
  );
};

export default CheckboxSelectionHeader;
